import { type CSSProperties, type ReactNode, useEffect, useRef } from "react";

import { useShallow } from "zustand/react/shallow";

import { clsx } from "../../utils/clsx";
import { TransitionGroup } from "../transition-group/transition-group";
import { CloseConfirmationModal } from "./close-confirmation-modal";
import { useRouteModalHistoryUpdates } from "./history/use-route-modal-history-updates";
import { useUserTriggeredModalHistoryUpdates } from "./history/use-user-triggered-modal-history-updates";
import { Modal } from "./modal";
import { ModalBackdrop } from "./modal-backdrop";
import { ModalsStackContextProvider } from "./modal-stack-context";
import { ModalStackOverlay } from "./modal-stack-overlay";
import { useModalBgClickDismiss } from "./operations/use-modal-bg-click-dismiss";
import { useModalCloseOperations } from "./operations/use-modal-close-operations";
import { useModalStateOperations } from "./operations/use-modal-state-operations";
import { RouteModalView } from "./route-modal-view";
import { useRouteModalsStore } from "./route-modals.store";
import { useTopmostModalSize } from "./use-max-modal-size";
import { UserTriggeredModalView } from "./user-triggered-modal-view";
import { useUserTriggeredModalsStore } from "./user-triggered-modals.store";

interface ModalStackProviderProps {
  children?: ReactNode;
}

const OFFSET_BETWEEN_MODALS = 24;
const BUTTON_PRIMARY = 0;

export const ModalStackProvider = ({ children }: ModalStackProviderProps) => {
  const { closeTopmostModal } = useModalCloseOperations();
  const { setUserTriggeredModalState, setRouteModalState } = useModalStateOperations();

  const { canDismissWithBackgroundClick } = useModalBgClickDismiss();

  useRouteModalHistoryUpdates();
  useUserTriggeredModalHistoryUpdates();

  const isRouteModalMounted = useRouteModalsStore((state) => state.isMounted);
  const openTriggeredModalIds = useUserTriggeredModalsStore(useShallow((state) => state.openIds.filter((id) => id in state.children)));
  const prevOpenTriggeredModalIds = useUserTriggeredModalsStore((state) => state.prevOpenIds);

  const hasModals = isRouteModalMounted || openTriggeredModalIds.length > 0;
  const modalCount = openTriggeredModalIds.length + (isRouteModalMounted ? 1 : 0);
  const prevModalCount = prevOpenTriggeredModalIds.length + (isRouteModalMounted ? 1 : 0);

  const modalsContainerRef = useRef<HTMLDivElement>(null);
  const [{ height, width }, updateMaxModalHeight] = useTopmostModalSize(modalsContainerRef);

  const expectDismissClickRef = useRef(false);

  useEffect(() => {
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === "Escape" && hasModals) {
        closeTopmostModal("user");
      }
    };

    document.addEventListener("keyup", onKeyUp);

    return () => document.removeEventListener("keyup", onKeyUp);
  }, [hasModals, closeTopmostModal]);

  useEffect(() => {
    document.addEventListener("mouseup", () => {
      expectDismissClickRef.current = false;
    });
  }, []);

  return (
    <ModalsStackContextProvider>
      {children}

      <Modal>
        <CloseConfirmationModal />
      </Modal>
      {/** biome-ignore lint/a11y/noStaticElementInteractions: mouse inputs for interacting with backdrop, keyboard handled separately */}
      <div
        className={clsx("fixed inset-0 z-10 flex flex-col transition-opacity select-none", !hasModals && "pointer-events-none opacity-0")}
        onMouseDown={(e) => {
          if (e.button !== BUTTON_PRIMARY) {
            return;
          }

          expectDismissClickRef.current = true;
        }}
        onMouseUp={(e) => {
          if (canDismissWithBackgroundClick() && expectDismissClickRef.current && e.button === BUTTON_PRIMARY) {
            closeTopmostModal("user");
          }
        }}
      >
        <ModalBackdrop />

        <div
          className={clsx(
            "absolute top-16 right-0 left-0 duration-100 sm:top-[var(--top-offset)]",
            prevModalCount < modalCount && "transition-[transform]",
          )}
          style={
            {
              "--top-offset": `calc(50vh - ${height / 2}px)`,
              transform: `translateY(-${(modalCount - 1) * OFFSET_BETWEEN_MODALS}px)`,
            } as CSSProperties
          }
          ref={modalsContainerRef}
        >
          <TransitionGroup
            className="!overflow-visible data-enter:animate-[modal-enter_0.2s] data-leave:animate-[modal-leave_0.2s]"
            duration={100}
            animation="custom"
          >
            {isRouteModalMounted && (
              <RouteModalView
                key="page-modal"
                depth={modalCount - 1}
                onContentResize={updateMaxModalHeight}
                onStateChange={setRouteModalState}
                onClose={closeTopmostModal}
              />
            )}
            {openTriggeredModalIds.map((id, index) => {
              const isOnTop = index === openTriggeredModalIds.length - 1;
              const shouldRenderStackOverlay = modalCount > 1 && isOnTop;

              return (
                <UserTriggeredModalView
                  key={`temporary-${id}`}
                  id={id}
                  depth={modalCount - (index + (isRouteModalMounted ? 1 : 0) + 1)}
                  onContentResize={updateMaxModalHeight}
                  onStateChange={setUserTriggeredModalState}
                  onClose={closeTopmostModal}
                  renderStackOverlay={
                    shouldRenderStackOverlay ? (
                      <ModalStackOverlay stackCount={modalCount} width={width} isGrowing={prevModalCount < modalCount} />
                    ) : null
                  }
                />
              );
            })}
          </TransitionGroup>
        </div>
      </div>
    </ModalsStackContextProvider>
  );
};
